export interface IAutomation<T extends AutomationTriggerType> {
  id?: string;
  name: string;
  accountId: string;
  status: AutomationStatus;
  ownerId: string;
  description: string;
  automationTask?: IAutomationTask;
  trigger?: IAutomationTrigger<T>;
  schedule?: IAutomationSchedule;

  isDraftMode?: boolean;
  missingItem?: string | boolean;
}

export interface IAutomationTrigger<T extends AutomationTriggerType> {
  objectType: T;
  operation: AutomationOperationByTargetType[T];
  manual?: boolean;
  scheduled?: boolean;
}

export type AutomationTriggerType = "goal" | "metric" | "user";

export type AutomationTriggerPayload = {
  name?: string;
  description?: string;
  ownerId?: string;
  status?: AutomationStatus;
  trigger?: IAutomationTrigger<AutomationTriggerType>;
  schedule?: IAutomationSchedule;
  automationTask?: IAutomationTask;
};

export type AutomationMagicFieldFormatType = "email" | "slack";

export type AutomationStatus = "enabled" | "disabled" | "execute";

export type AutomationWeekday = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday";

export type AutomationCadence = "daily" | "weekly" | "biweekly" | "monthly";

export enum UIAutomationStatus {
  enabled = "enabled",
  disabled = "disabled",
  execute = "execute",
  draft = "draft",
}

type PrependNextNum<A extends Array<unknown>> = A["length"] extends infer T ? (((t: T, ...a: A) => void) extends (...x: infer X) => void ? X : never) : never;

type EnumerateInternal<A extends Array<unknown>, N extends number> = { 0: A; 1: EnumerateInternal<PrependNextNum<A>, N> }[N extends A["length"] ? 0 : 1];

export type Enumerate<N extends number> = EnumerateInternal<[], N> extends (infer E)[] ? E : never;

export type NumberRange<FROM extends number, TO extends number> = Exclude<Enumerate<TO>, Enumerate<FROM>>;

export type DayOfMonth = NumberRange<1, 32>;

export type AutomationConditionBooleanOperator = "And" | "Or";

export interface IAutomationSchedule {
  cadence: AutomationCadence;
  time: Date;
  weekday: AutomationWeekday;
  monthDay: DayOfMonth;
}
export interface IAutomationCondition {
  hideNumericInput?: boolean;
  booleanOperator: AutomationConditionBooleanOperator;
  showDateNumericInput?: boolean;
  showDatePicker?: boolean;
  inputValue?: string;
  selectedProperty: ISelectedProperty;
  selectedOperator: AutomationRuleOperator;
  firstCondition: boolean;
  useOldValue?: boolean;
  compareDifferenceOperators?: string[];
  compareComboOperators?: AutomationRuleOperator[];
  selectedDifferenceOperator?: string;
  selectedCompareOperator?: AutomationRuleOperator;
  compareCombo?: string;
}

export interface ISelectedProperty {
  propertyName: AutomationTargetPropertyName;
  ruleOperators: AutomationRuleOperator[];
  dataType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: any; // this could literally be anything array,string,object
  predefinedValues?: string[] | { color: string; value: string }[];
  inputName?: string;
  inputValue?: string | number;
}

export interface INewAutomation {
  automation: {
    name: string;
    description: string;
  };
}

export interface IAutomationTask {
  accountId: string;
  id?: string;
  automationId: string;
  rule?: IAutomationRule;
  // todo-user-automations
  rules?: IAutomationRule;
  actions?: IAutomationAction[];
  nextAutomationTask?: INextAutomationTask;
}

export interface INextAutomationTask {
  accountId: string;
  automationId: string;
  rule: IAutomationRule;
  actions?: IAutomationAction[];
}

export interface IAutomationRule {
  id?: string;
  accountId?: string;
  ruleBounds: IAutomationRuleBounds[];
  booleanLogic: AutomationConditionBooleanOperator;
}

export type AutomationRuleOperator =
  | "equals"
  | "greater"
  | "notEqual"
  | "less"
  | "compareTo"
  | "previousValue"
  | "inLast"
  | "notInLast"
  | "differenceEquals"
  | "differenceGreater"
  | "differenceNotEqual"
  | "differenceLess"
  | "difference";

export interface IAutomationRuleBounds {
  fieldName: string;
  operator: AutomationRuleOperator;
  value: any; // could be literally anything
  dataType?: string;
  valueName?: string;
  customField?: boolean;
  customFieldDisplayName?: string;
  isCustomFieldDeleted?: boolean;
  useOldValue?: boolean;
}

export interface IFlatAutomationTasks {
  id: string;
  rules: IAutomationRule;
  actions: IAutomationAction[];
}

export interface IAutomationAction {
  id?: string;
  accountId?: string;
  createdBy?: string;
  actionType: AutomationActionTypeByTargetType[AutomationTriggerType];
  name: string;
  metadata?: IAutomationActionMetadata;
}

export interface IAutomationActionRecipients {
  users: string[];
  teams: string[];
  creator: boolean;
  assignee: boolean;
  targetedUser: boolean;
}
export interface IAutomationActionMetadata {
  channel?: string;
  message?: string;
  email?: string;
  subject?: string;
  name?: string;
  description?: string;
  parent?: "current" | string;
  assignee?: string;
  tags?: string[];
  recipients?: IAutomationActionRecipients;
  parentType?: AutomationTriggerType;
  roleId?: string;
  workspace?: string;
}

type AutomationTargetPropertyName =
  | "Attainment"
  | "AggregatedAttainment"
  | "ChildrenCount"
  | "MetricsCount"
  | "TasksCount"
  | "DesignScore"
  | "LatestSnapshotAt"
  | "DateTo"
  | "SessionID"
  | "Name"
  | "Description"
  | "OwnerID"
  | "DateCreated"
  | "DateFrom"
  | "CreatedByID"
  | "ModifiedByID"
  | "Tags"
  | "Private"
  | "Parent";

export interface IAutomationTargetProperty {
  propertyName: AutomationTargetPropertyName;
  ruleOperators: string[];
  dataType: string;
  predefinedValues?: string[] | { targetGroup: string };
  input?: string | string[];
}

export interface ITargetPropertiesTypes {
  properties: IAutomationTargetProperty[];
  customFieldsProperties: IAutomationTargetProperty[];
}

export interface IAutomationHistoryQueryParams {
  skip?: number;
  take?: number;
  status?: string;
}

export enum AutomationHistoryStatusEnum {
  AutomationRunning = 0,
  AutomationSuccess = 1,
  AutomationFail = 2,
  AutomationPartialSuccess = 3,
}

export interface IAutomationHistory {
  targetId: string;
  targetType: AutomationTriggerType;
  status: AutomationHistoryStatusEnum;
  lastExecutionTime: string;
  targetName?: string;
  targetSessionId?: string;
  targetParentId?: string;
}

export interface IAutomationTargetPropertiesParams {
  targetType: AutomationTriggerType;
}

export type AutomationOperationByTargetType = {
  goal: "created" | "modified" | "deleted" | "progress changed";
  metric: "created" | "modified" | "deleted" | "progress changed";
  user: "created" | "deactivated";
};

export type AutomationActionTypeByTargetType = {
  goal: "email" | "create_task" | "delete_item" | "send_slack_message" | "tag";
  metric: "email" | "create_task" | "delete_item" | "send_slack_message";
  user: "email" | "delete_item" | "assign_role" | "unassign_role";
};

type AutomationTargetEvent = {
  [AutomationTargetType in AutomationTriggerType]: AutomationOperationByTargetType[AutomationTargetType];
};
export interface ISupportedAutomationTarget<T extends keyof AutomationTargetEvent> {
  target: T;
  events: AutomationTargetEvent[T][];
}

export type ISupportedAutomationActionTypes = {
  [AutomationTargetType in AutomationTriggerType]: AutomationActionTypeByTargetType[AutomationTargetType];
};
export interface IAutomationTargetPropertiesWithAction<T extends keyof AutomationTargetEvent> {
  supportedTargets: ISupportedAutomationTarget<T>[];
  totalCount: number;
}

export interface ISupportedAutomationActionTypesRes<T extends keyof AutomationTargetEvent> {
  supportedActionTypes: ISupportedAutomationActionTypes[T][];
  totalCount: number;
}

export interface IAutomationIndicators {
  loading: boolean;
  patching: boolean;
  creating: boolean;
  deleting: boolean;
  toggling: boolean;
  executing: boolean;
  cloning: boolean;
}

export type AutomationMagicFieldTypeName = "Objective" | "Metric" | "Session" | "Assignee" | "Creator" | "User";

export interface IMagicField {
  typeName: AutomationMagicFieldTypeName;
  properties: IMagicFieldProperties[];
}

export interface IMagicFieldProperties {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  DisplayName: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  TemplateContent: string;
}

export type PropertiesDictionaryKeys =
  | "ChildrenCount"
  | "Attainment"
  | "SessionID"
  | "OwnerID"
  | "OwnerIds"
  | "DateCreated"
  | "DateFrom"
  | "DateTo"
  | "CreatedByID"
  | "ModifiedByID"
  | "AggregatedAttainment"
  | "MetricsCount"
  | "Parent"
  | "TasksCount"
  | "GoalID"
  | "SoftDueDate"
  | "InitialValue"
  | "DueDate"
  | "LatestSnapshotAt"
  | "Goal_SessionID"
  | "IsActive";

export type PropertiesDictionary = Record<PropertiesDictionaryKeys, string>;

export interface IPropertyNames {
  session: string;
  name: string;
  description: string;
  ownerId: string;
  ownerIds: string;
  dateCreated: string;
  dateFrom: string;
  dateTo: string;
  createdById: string;
  modifiedById: string;
  attainment: string;
  aggregatedAttainment: string;
  childrenCount: string;
  metricsCount: string;
  private: string;
  parent: string;
  tasksCount: string;
  objective: string;
  goalSessionId: string;
}

export interface ICustomFieldProperties {
  selectAssignee: string;
  boolean: string;
  multiselect: string;
  dropdown: string;
}
export interface ISlackChannels {
  workspace: string;
  channels: ISlackChannel[];
}

export interface IHasSlackIntegration {
  hasIntegration: boolean;
}

export interface ISlackChannel {
  id: string;
  name: string;
}

export interface ISlackConfig {
  channel: string;
  activities: ISlackConfigActivity;
}

export interface ISlackConfigActivity {
  [activityName: string]: {
    display: string;
    enabled: boolean;
  };
}
